import { OpenFile, Viewer, Worker } from "@react-pdf-viewer/core";
import { keycloak } from "@/utils/lib/keycloak.ts";
import { useParams, useSearchParams } from "react-router-dom";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { useEffect, useState } from "react";

// Import styles
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg"];

const IMAGE_LOAD_ERROR =
  "Failed to load the image. Please try again or use the Download button.";

function isImageFile(name: string | undefined) {
  const extension = name?.split(".").pop()?.toLowerCase() ?? "";
  return IMAGE_EXTENSIONS.includes(extension);
}

/**
 * The download endpoint needs the Bearer token, so an img src cannot point at it.
 * The image is fetched as a blob and shown through an object URL instead.
 */
function ImagePreview({
  fileUrl,
  fileName,
}: Readonly<{ fileUrl: string; fileName: string }>) {
  const [objectUrl, setObjectUrl] = useState<string>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let url: string | undefined;

    fetch(fileUrl, {
      headers: { Authorization: `Bearer ${keycloak.token}` },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.blob();
      })
      .then((blob) => {
        url = URL.createObjectURL(blob);
        setObjectUrl(url);
      })
      .catch((error: unknown) => {
        /* Leaving the page aborts the request; that is not a load failure. */
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setFailed(true);
      });

    return () => {
      controller.abort();
      if (url) URL.revokeObjectURL(url);
    };
  }, [fileUrl]);

  let content = <p>Loading...</p>;
  if (failed) {
    content = <p className="text-red-500">{IMAGE_LOAD_ERROR}</p>;
  } else if (objectUrl) {
    content = (
      <img
        src={objectUrl}
        alt={fileName}
        className="max-h-full max-w-full object-contain"
      />
    );
  }

  return (
    <div className="flex h-[calc(100vh-64px)] items-center justify-center">
      {content}
    </div>
  );
}

const Preview: React.FC = () => {
  const { path, name } = useParams();
  const [searchParams] = useSearchParams();
  const fileName = searchParams.get("fileName");
  const prefix = searchParams.get("prefix");
  const fileUrl =
    import.meta.env.VITE_API_BASE +
    `/${prefix ? prefix : "e2e"}/crud/media/download?path=/${path}/${name}`;

  // Create new plugin instance
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    toolbarPlugin: {
      getFilePlugin: {
        fileNameGenerator: (file: OpenFile) => {
          // `file.name` is the URL of opened file
          const originalFileName = file.name.substring(
            file.name.lastIndexOf("/") + 1,
          );
          return fileName ?? originalFileName;
        },
      },
    },
  });

  if (isImageFile(name)) {
    return <ImagePreview fileUrl={fileUrl} fileName={fileName ?? name ?? ""} />;
  }

  return (
    <div className="h-[calc(100vh-64px)]">
      <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
        <Viewer
          httpHeaders={{
            Authorization: `Bearer ${keycloak.token}`,
          }}
          fileUrl={fileUrl}
          plugins={[
            // Register plugins
            defaultLayoutPluginInstance,
          ]}
        />
      </Worker>
    </div>
  );
};

export default Preview;
